import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Gift,
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  Zap,
  Star,
  DollarSign,
  Percent,
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Send,
  MessageSquare,
  Award,
  ShoppingBag,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Bell,
  Calendar,
  UserMinus,
  UserPlus,
  RefreshCw,
  Settings,
  Eye,
  ChevronRight,
  Phone,
  Mail,
  Megaphone,
  Tag,
  PartyPopper,
  Trash2,
  Plus,
  Image,
  Link,
  X,
  Upload,
  Camera,
  Loader2,
  History,
  AlertCircle,
  Merge,
  Copy,
  Check,
  UserCheck
} from 'lucide-react';

// WhatsApp Message Templates (same as Customers.js)
const MESSAGE_TEMPLATES = {
  points_reminder: {
    name: '📊 Recordatorio de Puntos',
    template: `🐸 *¡Hola {nombre}!*

Soy *Froggy* de *Mr. Frogs* 🍽️

📊 *Tu saldo actual:*
⭐ *{puntos} puntos* disponibles
💰 Valor: *$\{valor} MXN*

🎁 ¡Canjéalos por *premios GRATIS*!

👉 *Consulta tu tarjeta VIP aquí:*
{link}

¡Te esperamos pronto! 🐸✨

_Mr. Frogs - El mejor sabor del mar_`
  },
  vip_welcome: {
    name: '🏆 Bienvenida VIP',
    template: `🐸 *¡Hola {nombre}!*

¡Felicidades! 🎉 Eres parte de nuestro *Club VIP Mr. Frogs*

🏆 *Tu nivel:* {nivel}
⭐ *Puntos:* {puntos}
🛒 *Visitas:* {visitas}

✨ *Beneficios exclusivos:*
• Acumula puntos con cada compra
• Canjea por premios gratis
• Promociones especiales

👉 *Accede a tu tarjeta VIP:*
{link}

¡Gracias por tu preferencia! 🐸💚

_Mr. Frogs - El mejor sabor del mar_`
  },
  promo_special: {
    name: '🔥 Promoción Especial',
    template: `🐸 *¡{nombre}!*

🔥 *PROMOCIÓN ESPECIAL* solo para clientes VIP

Con tus *{puntos} puntos* puedes canjear increíbles premios 🎁

⭐ ¡No dejes que se acumulen, úsalos!

👉 *Canjea aquí:*
{link}

¡Te esperamos! 🍽️

_Mr. Frogs - El mejor sabor del mar_`
  },
  new_customer: {
    name: '👋 Nuevo Cliente',
    template: `🐸 *¡Hola {nombre}!*

¡Bienvenido a la familia *Mr. Frogs*! 🎉

Ya eres parte de nuestro *Club VIP* donde:
✅ Acumulas puntos con cada compra
✅ Canjeas por premios gratis
✅ Recibes promociones exclusivas

👉 *Consulta tus puntos aquí:*
{link}

¡Nos vemos pronto! 🐸💚

_Mr. Frogs - El mejor sabor del mar_`
  },
  inactive_recovery: {
    name: '💔 Recuperar Inactivo',
    template: `🐸 *¡Hola {nombre}!*

¡Te extrañamos en *Mr. Frogs*! 💚

📊 Tienes *{puntos} puntos* esperándote
💰 Valor: *$\{valor} MXN*

🎁 ¡Ven a canjearlos antes de que expiren!

👉 *Tu tarjeta VIP:*
{link}

¡Te esperamos de vuelta! 🍽️✨

_Mr. Frogs - El mejor sabor del mar_`
  }
};

const LoyaltyManager = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showOnlyRedeemable, setShowOnlyRedeemable] = useState(false);
  const [redemptionHistory, setRedemptionHistory] = useState({ redemptions: [], stats: {} });
  const [inactiveCustomers, setInactiveCustomers] = useState([]);
  const [expiringPoints, setExpiringPoints] = useState([]);
  const [activeTab, setActiveTab] = useState('products'); // products, campaigns, analytics, notifications
  const [savingProduct, setSavingProduct] = useState(null);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    notification_type: 'general',
    target: 'all',
    expires_at: '',
    image_url: ''  // Optional image URL
  });

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedProductForImage, setSelectedProductForImage] = useState(null);
  const [uploadingNotifImage, setUploadingNotifImage] = useState(false);
  
  // Campaign search and filter state
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('all'); // all, with_points, no_points, inactive, new
  const [allCustomers, setAllCustomers] = useState([]);
  
  // Duplicates state
  const [duplicates, setDuplicates] = useState({ groups: [], total_duplicates: 0 });
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [mergingDuplicates, setMergingDuplicates] = useState(false);

  // Portal visits tracking state
  const [recentVisits, setRecentVisits] = useState([]);
  const [visitStats, setVisitStats] = useState({ today_visits: 0, week_visits: 0, unique_visitors_week: 0 });
  const [lastVisitCount, setLastVisitCount] = useState(0);

  // WhatsApp Masivo Modal State
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('points_reminder');
  const [whatsAppCustomers, setWhatsAppCustomers] = useState([]);
  const [whatsAppFilterType, setWhatsAppFilterType] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [sendingToCustomer, setSendingToCustomer] = useState(null);
  const [contactStatus, setContactStatus] = useState({});
  const [whatsAppSearch, setWhatsAppSearch] = useState(''); // Search in WhatsApp Masivo

  // Helper function to normalize phone for display
  const normalizePhoneDisplay = (phone) => {
    if (!phone) return '';
    // Remove all non-digits
    let digits = phone.replace(/\D/g, '');
    // Remove country code 52 if present
    if (digits.startsWith('52') && digits.length > 10) {
      digits = digits.substring(2);
    }
    // Return last 10 digits
    if (digits.length > 10) {
      digits = digits.slice(-10);
    }
    return digits;
  };

  // Helper function to send WhatsApp and register in CRM
  const sendWhatsAppWithTracking = async (customer, messageType, messageContent) => {
    const phone = customer.phone?.replace(/\D/g, '') || '';
    if (!phone) {
      toast.error('Cliente sin teléfono');
      return;
    }
    
    const fullPhone = phone.startsWith('52') ? phone : `52${phone}`;
    const pts = customer.points_balance || 0;
    
    // Generate message based on type
    let message = '';
    if (messageType === 'vip_card') {
      message = pts > 0 
        ? `¡Hola ${customer.name}! 🐸🎁 Tienes ${pts.toLocaleString()} puntos en MR FROGS (= $${(pts * POINTS_VALUE).toFixed(2)}). ¡Ven a canjearlos! Tu tarjeta VIP: ${window.location.origin}/mi-tarjeta`
        : `¡Hola ${customer.name}! 🐸 ¡Te invitamos a unirte a nuestro Club VIP en MR FROGS! Acumula puntos y canjéalos por productos gratis. Tu tarjeta: ${window.location.origin}/mi-tarjeta`;
    } else {
      message = messageContent || `¡Hola ${customer.name}! 🐸`;
    }
    
    // Open WhatsApp
    window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(message)}`, '_blank');
    
    // Register in CRM
    try {
      await api.post('/communications', {
        customer_id: customer.customer_id || customer.id,
        customer_name: customer.name,
        customer_phone: phone,
        message_type: messageType,
        channel: 'whatsapp',
        message_content: messageType === 'vip_card' ? 'Tarjeta VIP enviada' : messageContent
      });
      toast.success('WhatsApp enviado y registrado en CRM');
    } catch (error) {
      console.error('Error registering communication:', error);
      // WhatsApp already opened, just log the error
    }
  };

  // Get app link for customer portal
  const getAppLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/mi-tarjeta`;
  };

  // Generate personalized message for a customer
  const generateMessage = (customer, templateKey) => {
    const template = MESSAGE_TEMPLATES[templateKey]?.template || MESSAGE_TEMPLATES.points_reminder.template;
    const points = customer.points_balance || customer.points_available || customer.points || 0;
    const valor = (points * POINTS_VALUE).toFixed(2);
    const level = points >= 5000 ? 'Diamante' : points >= 2000 ? 'Oro' : points >= 500 ? 'Plata' : 'Bronce';
    
    return template
      .replace(/{nombre}/g, customer.name || 'Cliente')
      .replace(/{puntos}/g, points.toLocaleString())
      .replace(/\{valor}/g, valor)
      .replace(/{nivel}/g, level)
      .replace(/{visitas}/g, customer.visit_count || customer.visits || 0)
      .replace(/{link}/g, getAppLink());
  };

  // Copy message to clipboard
  const copyMessage = (customer) => {
    const message = generateMessage(customer, selectedTemplate);
    navigator.clipboard.writeText(message);
    setCopiedId(customer.customer_id || customer.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Mensaje copiado');
  };

  // Open WhatsApp and register in CRM (for mass messaging)
  const openWhatsAppMass = async (customer) => {
    const phone = customer.phone?.replace(/\D/g, '') || '';
    if (!phone) {
      toast.error('Cliente sin teléfono');
      return;
    }
    
    setSendingToCustomer(customer.customer_id || customer.id);
    
    const fullPhone = phone.startsWith('52') ? phone : `52${phone}`;
    const message = generateMessage(customer, selectedTemplate);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${fullPhone}?text=${encodedMessage}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Register in CRM
    try {
      await api.post('/communications', {
        customer_id: customer.customer_id || customer.id,
        customer_name: customer.name,
        customer_phone: phone,
        message_type: selectedTemplate === 'vip_welcome' ? 'vip_card' : 
                      selectedTemplate === 'new_customer' ? 'welcome' :
                      selectedTemplate === 'promo_special' ? 'promo' : 'reminder',
        channel: 'whatsapp',
        message_content: MESSAGE_TEMPLATES[selectedTemplate]?.name || 'Mensaje personalizado'
      });
      toast.success('Enviado y registrado en CRM');
    } catch (error) {
      console.error('Error registering communication:', error);
    }
    
    setSendingToCustomer(null);
  };

  // Fetch contact status for CRM tracking
  const fetchContactStatus = async () => {
    try {
      const res = await api.get('/communications/customers/contact-status?days=30');
      const statusMap = {};
      for (const c of res.data.customers || []) {
        statusMap[c.customer_id] = {
          contacted: c.contacted_in_period,
          vipCardSent: c.vip_card_sent,
          lastContact: c.last_contact,
          contactCount: c.contact_count
        };
      }
      setContactStatus(statusMap);
    } catch (error) {
      console.error('Error loading contact status:', error);
    }
  };

  // Open WhatsApp Mass Modal
  const openWhatsAppMassModal = () => {
    setWhatsAppCustomers(allCustomers.filter(c => c.phone));
    setWhatsAppFilterType('all');
    fetchContactStatus();
    setShowWhatsAppModal(true);
  };

  // Points configuration
  const POINTS_VALUE = 0.10; // 1 point = $0.10

  // Helper function to get full image URL (works in all environments)
  const getImageUrl = (url) => {
    if (!url) return '';
    // If URL starts with /api/, it's a relative API path
    if (url.startsWith('/api/')) {
      // Use env variable in dev, or current origin in production
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      return `${baseUrl}${url}`;
    }
    // If URL is already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // For any other relative URL, prepend base URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // Image upload function
  const uploadImage = async (file, type = 'product') => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // Handle product image upload
  const handleProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProductForImage) return;

    // Validate file
    if (!file.type.match(/^image\/(jpeg|png|webp|jpg)$/)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const result = await uploadImage(file, 'product');
      // Use relative URL so it works in any environment
      const imageUrl = result.url;
      
      // Update product with image
      await api.post('/customer-portal/admin/set-redeemable', {
        product_id: selectedProductForImage.product_id,
        points_cost: selectedProductForImage.points_cost || Math.ceil(selectedProductForImage.price / POINTS_VALUE * 0.8),
        is_active: selectedProductForImage.is_active || true,
        image_url: imageUrl
      });

      toast.success('¡Imagen guardada!');
      setShowImageModal(false);
      setSelectedProductForImage(null);
      await loadProducts();
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle notification image upload
  const handleNotificationImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.match(/^image\/(jpeg|png|webp|jpg)$/)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingNotifImage(true);
    try {
      const result = await uploadImage(file, 'notification');
      // Use relative URL so it works in any environment
      const imageUrl = result.url;
      setNewNotification(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('¡Imagen cargada!');
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingNotifImage(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh recent visits every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'campaigns') {
        loadRecentVisits();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, lastVisitCount]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadProducts(),
        loadRedemptionHistory(),
        loadInactiveCustomers(),
        loadExpiringPoints(),
        loadNotifications(),
        loadDuplicates(),
        loadRecentVisits(),
        loadVisitStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await api.get('/customer-portal/admin/redeemable-products');
      setProducts(res.data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const res = await api.get('/customer-portal/admin/notifications');
      setNotifications(res.data.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const createNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      toast.error('Completa título y mensaje');
      return;
    }
    try {
      // Prepare data with expiration date if set
      const notifData = {
        ...newNotification,
        expires_at: newNotification.expires_at ? new Date(newNotification.expires_at + 'T23:59:59').toISOString() : null,
        image_url: newNotification.image_url || null
      };
      await api.post('/customer-portal/admin/notifications', notifData);
      toast.success('¡Notificación enviada!');
      setShowNotificationModal(false);
      setNewNotification({ title: '', message: '', notification_type: 'general', target: 'all', expires_at: '', image_url: '' });
      loadNotifications();
    } catch (error) {
      toast.error('Error al enviar notificación');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/customer-portal/admin/notifications/${notificationId}`);
      toast.success('Notificación eliminada');
      loadNotifications();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Toggle notification active status
  const toggleNotificationActive = async (notification, isActive) => {
    try {
      await api.put(`/customer-portal/admin/notifications/${notification.notification_id}`, {
        is_active: isActive
      });
      toast.success(isActive ? 'Notificación activada' : 'Notificación desactivada');
      loadNotifications();
    } catch (error) {
      toast.error('Error al actualizar notificación');
    }
  };

  const loadRedemptionHistory = async () => {
    try {
      const res = await api.get('/customer-portal/admin/redemption-history?days=30');
      setRedemptionHistory(res.data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const loadInactiveCustomers = async () => {
    try {
      const res = await api.get('/crm-intelligence/alerts');
      // Get ALL at-risk and win-back customers (no limit)
      const inactive = [
        ...(res.data.alerts?.at_risk || []),
        ...(res.data.alerts?.win_back || [])
      ];
      setInactiveCustomers(inactive);
    } catch (error) {
      console.error('Error loading inactive customers:', error);
    }
  };

  const loadExpiringPoints = async () => {
    try {
      // Get ALL customers
      const res = await api.get('/customers');
      const customers = Array.isArray(res.data) ? res.data : (res.data.customers || []);
      
      // Store all customers for the campaign tab
      const allWithPoints = customers.map(c => ({
        ...c,
        points_balance: (c.points_earned || 0) - (c.points_redeemed || 0)
      }));
      setAllCustomers(allWithPoints);
      
      // Filter those with significant points (>100) for the points list
      const withPoints = allWithPoints
        .filter(c => c.points_balance > 100)
        .sort((a, b) => b.points_balance - a.points_balance);
      setExpiringPoints(withPoints);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Duplicate detection and merge functions
  const loadDuplicates = async () => {
    try {
      const res = await api.get('/customers/duplicates/detect');
      setDuplicates(res.data);
    } catch (error) {
      console.error('Error loading duplicates:', error);
    }
  };

  const mergeDuplicates = async (primaryId, secondaryIds) => {
    setMergingDuplicates(true);
    try {
      await api.post('/customers/duplicates/merge', {
        primary_customer_id: primaryId,
        secondary_customer_ids: secondaryIds
      });
      toast.success('Clientes fusionados exitosamente');
      await loadDuplicates();
      await loadExpiringPoints(); // Refresh customer list
    } catch (error) {
      toast.error('Error al fusionar clientes');
      console.error(error);
    } finally {
      setMergingDuplicates(false);
    }
  };

  const normalizeAllPhones = async () => {
    try {
      const res = await api.post('/customers/normalize-all');
      toast.success(res.data.message);
      await loadDuplicates();
      await loadExpiringPoints();
    } catch (error) {
      toast.error('Error al normalizar teléfonos');
    }
  };

  // Portal visit tracking functions
  const loadRecentVisits = async () => {
    try {
      const res = await api.get('/customer-portal/admin/recent-visits?limit=20&since_minutes=60');
      const newVisits = res.data.visits || [];
      
      // Check if there are new visits since last check
      if (newVisits.length > lastVisitCount && lastVisitCount > 0) {
        // Play notification sound for new visits
        playVisitNotificationSound();
        const newestVisit = newVisits[0];
        toast.success(`🐸 ${newestVisit.customer_name} entró al portal`, {
          description: `Sección: ${getSectionLabel(newestVisit.section)}`
        });
      }
      
      setRecentVisits(newVisits);
      setLastVisitCount(newVisits.length);
    } catch (error) {
      console.error('Error loading recent visits:', error);
    }
  };

  const loadVisitStats = async () => {
    try {
      const res = await api.get('/customer-portal/admin/visit-stats');
      setVisitStats(res.data);
    } catch (error) {
      console.error('Error loading visit stats:', error);
    }
  };

  const playVisitNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.error('Audio error:', e);
    }
  };

  const getSectionLabel = (section) => {
    const labels = {
      'card': 'Mi Tarjeta',
      'redeem': 'Canjear Puntos',
      'history': 'Historial',
      'notifications': 'Notificaciones'
    };
    return labels[section] || section;
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return `Hace ${Math.floor(diffHours / 24)}d`;
  };

  const toggleRedeemable = async (product, isActive) => {
    setSavingProduct(product.product_id);
    try {
      if (isActive) {
        // Calculate suggested points (aim for 60-70% margin products)
        const suggestedPoints = Math.ceil(product.price / POINTS_VALUE * 0.8);
        await api.post('/customer-portal/admin/set-redeemable', {
          product_id: product.product_id,
          points_cost: product.points_cost || suggestedPoints,
          is_active: true
        });
        toast.success(`${product.name} ahora es canjeable`);
      } else {
        await api.delete(`/customer-portal/admin/remove-redeemable/${product.product_id}`);
        toast.success(`${product.name} removido de canjeables`);
      }
      await loadProducts();
    } catch (error) {
      toast.error('Error al actualizar producto');
    } finally {
      setSavingProduct(null);
    }
  };

  const updatePointsCost = async (product, newPoints) => {
    if (!newPoints || newPoints < 1) return;
    setSavingProduct(product.product_id);
    try {
      await api.post('/customer-portal/admin/set-redeemable', {
        product_id: product.product_id,
        points_cost: parseInt(newPoints),
        is_active: true
      });
      toast.success('Puntos actualizados');
      await loadProducts();
    } catch (error) {
      toast.error('Error al actualizar puntos');
    } finally {
      setSavingProduct(null);
    }
  };

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category))].filter(Boolean);
    return cats.sort();
  }, [products]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
      const matchesRedeemable = !showOnlyRedeemable || p.is_redeemable;
      return matchesSearch && matchesCategory && matchesRedeemable;
    });
  }, [products, searchTerm, categoryFilter, showOnlyRedeemable]);

  // Stats calculations
  const stats = useMemo(() => {
    const redeemableCount = products.filter(p => p.is_redeemable).length;
    const activeCount = products.filter(p => p.is_active).length;
    const highMarginProducts = products.filter(p => p.margin_percent >= 60).length;
    
    return {
      total: products.length,
      redeemable: redeemableCount,
      active: activeCount,
      highMargin: highMarginProducts,
      redemptionsThisMonth: redemptionHistory.stats?.completed || 0,
      pointsRedeemed: redemptionHistory.stats?.total_points_redeemed || 0,
      completionRate: redemptionHistory.stats?.completion_rate || 0
    };
  }, [products, redemptionHistory]);

  const getMarginColor = (margin) => {
    if (margin >= 70) return 'text-green-600 bg-green-50';
    if (margin >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getMarginIcon = (margin) => {
    if (margin >= 70) return <TrendingUp className="w-4 h-4" />;
    if (margin >= 50) return <TrendingDown className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getDaysAgo = (dateStr) => {
    if (!dateStr) return 999;
    const date = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };

  // Filter customers based on campaign filter and search
  const getFilteredCampaignCustomers = () => {
    let filtered = allCustomers;
    
    // Apply filter
    switch (campaignFilter) {
      case 'with_points':
        filtered = filtered.filter(c => c.points_balance > 0);
        break;
      case 'no_points':
        filtered = filtered.filter(c => !c.points_balance || c.points_balance === 0);
        break;
      case 'inactive':
        filtered = filtered.filter(c => {
          if (!c.last_visit) return true;
          const days = Math.floor((new Date() - new Date(c.last_visit)) / (1000*60*60*24));
          return days > 30;
        });
        break;
      case 'new':
        filtered = filtered.filter(c => {
          if (!c.created_at) return false;
          const days = Math.floor((new Date() - new Date(c.created_at)) / (1000*60*60*24));
          return days <= 30;
        });
        break;
      default:
        break;
    }
    
    // Apply search
    if (campaignSearch) {
      const search = campaignSearch.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(search) ||
        c.phone?.includes(campaignSearch)
      );
    }
    
    // Sort by points descending
    return filtered.sort((a, b) => (b.points_balance || 0) - (a.points_balance || 0));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Gift className="w-8 h-8 text-green-600" />
          Programa de Lealtad
        </h1>
        <p className="text-gray-500 mt-1">Gestiona productos canjeables y estrategias de retención</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Productos Canjeables</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Gift className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Canjes este mes</p>
                <p className="text-2xl font-bold text-blue-600">{stats.redemptionsThisMonth}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ShoppingBag className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Puntos Canjeados</p>
                <p className="text-2xl font-bold text-purple-600">{stats.pointsRedeemed.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Alto Margen</p>
                <p className="text-2xl font-bold text-amber-600">{stats.highMargin}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'products', label: 'Productos Canjeables', icon: Gift },
          { id: 'notifications', label: 'Notificaciones', icon: Bell },
          { id: 'campaigns', label: 'Recuperar Clientes', icon: UserPlus },
          { id: 'analytics', label: 'Análisis', icon: BarChart3 }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap ${activeTab === tab.id ? 'bg-green-600 hover:bg-green-700' : ''}`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
            {tab.id === 'notifications' && notifications.length > 0 && (
              <Badge className="ml-2 bg-red-500">{notifications.length}</Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          {/* Strategy Tips Card */}
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Sparkles className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-800">💡 Estrategia Inteligente</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Activa productos con <strong>margen &gt;60%</strong> para canjes. 
                    El cliente percibe alto valor mientras tú mantienes rentabilidad. 
                    <strong> Bebidas y postres</strong> son ideales porque tienen bajo costo y alta percepción de valor.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar producto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="all">Todas las categorías</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showOnlyRedeemable}
                    onCheckedChange={setShowOnlyRedeemable}
                  />
                  <Label className="text-sm">Solo canjeables</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Configurar Productos</CardTitle>
              <CardDescription>
                Activa los productos que pueden canjearse con puntos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-gray-600">Producto</th>
                      <th className="pb-3 font-medium text-gray-600 text-center">Imagen</th>
                      <th className="pb-3 font-medium text-gray-600 text-right">Precio</th>
                      <th className="pb-3 font-medium text-gray-600 text-center">Margen</th>
                      <th className="pb-3 font-medium text-gray-600 text-center">Puntos</th>
                      <th className="pb-3 font-medium text-gray-600 text-center">Activo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr key={product.product_id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium">{product.name}</p>
                              <p className="text-sm text-gray-500">{product.category}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex justify-center">
                            <button
                              onClick={() => {
                                setSelectedProductForImage(product);
                                setShowImageModal(true);
                              }}
                              className="relative w-14 h-14 rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 hover:border-green-500 transition-colors group"
                            >
                              {product.image_url ? (
                                <>
                                  <img src={getImageUrl(product.image_url)} alt="" className="w-full h-full object-cover" />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Camera className="w-5 h-5 text-white" />
                                  </div>
                                </>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Upload className="w-5 h-5 text-gray-400 group-hover:text-green-500 transition-colors" />
                                </div>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="py-3 text-right font-medium">
                          ${product.price?.toFixed(2)}
                        </td>
                        <td className="py-3">
                          <div className="flex justify-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${getMarginColor(product.margin_percent)}`}>
                              {getMarginIcon(product.margin_percent)}
                              {product.margin_percent}%
                            </span>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex justify-center">
                            {product.is_redeemable ? (
                              <Input
                                type="number"
                                value={product.points_cost || ''}
                                onChange={(e) => {
                                  // Update local state immediately
                                  const newProducts = products.map(p => 
                                    p.product_id === product.product_id 
                                      ? {...p, points_cost: parseInt(e.target.value) || 0}
                                      : p
                                  );
                                  setProducts(newProducts);
                                }}
                                onBlur={(e) => updatePointsCost(product, e.target.value)}
                                className="w-24 text-center"
                                min="1"
                              />
                            ) : (
                              <span className="text-gray-400 text-sm">
                                ~{product.suggested_points} pts
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex justify-center">
                            <Switch
                              checked={product.is_active || false}
                              onCheckedChange={(checked) => toggleRedeemable(product, checked)}
                              disabled={savingProduct === product.product_id}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredProducts.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No se encontraron productos
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Acciones Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={async () => {
                    // Activate all high margin products
                    const highMargin = products.filter(p => p.margin_percent >= 65 && !p.is_active);
                    for (const p of highMargin.slice(0, 10)) {
                      await toggleRedeemable(p, true);
                    }
                    toast.success(`${Math.min(highMargin.length, 10)} productos activados`);
                  }}
                >
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  <span>Activar Alto Margen (&gt;65%)</span>
                  <span className="text-xs text-gray-500">
                    {products.filter(p => p.margin_percent >= 65 && !p.is_active).length} productos
                  </span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={async () => {
                    // Activate all beverages
                    const bebidas = products.filter(p => 
                      p.category?.toLowerCase().includes('bebida') && !p.is_active
                    );
                    for (const p of bebidas) {
                      await toggleRedeemable(p, true);
                    }
                    toast.success(`${bebidas.length} bebidas activadas`);
                  }}
                >
                  <ShoppingBag className="w-6 h-6 text-blue-600" />
                  <span>Activar Bebidas</span>
                  <span className="text-xs text-gray-500">
                    Alta percepción de valor
                  </span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={async () => {
                    // Activate all desserts
                    const postres = products.filter(p => 
                      p.category?.toLowerCase().includes('postre') && !p.is_active
                    );
                    for (const p of postres) {
                      await toggleRedeemable(p, true);
                    }
                    toast.success(`${postres.length} postres activados`);
                  }}
                >
                  <Gift className="w-6 h-6 text-purple-600" />
                  <span>Activar Postres</span>
                  <span className="text-xs text-gray-500">
                    Ideales para fidelización
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-4">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Megaphone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-800">📢 Comunica con tus clientes</h3>
                  <p className="text-sm text-purple-700 mt-1">
                    Envía promociones, avisos de puntos y mensajes personalizados. 
                    Los clientes verán las notificaciones al entrar a su <strong>Tarjeta VIP</strong>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Create Notification Button */}
          <Button 
            onClick={() => setShowNotificationModal(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Notificación
          </Button>

          {/* Notifications List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5 text-purple-500" />
                Notificaciones Activas ({notifications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Bell className="w-8 h-8 text-gray-300" />
                  </div>
                  <p className="text-gray-500">No hay notificaciones activas</p>
                  <p className="text-gray-400 text-sm mt-1">Crea una para comunicarte con tus clientes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.notification_id}
                      className={`p-4 rounded-lg border transition-all ${notif.is_active !== false ? 'bg-white hover:shadow-md' : 'bg-gray-50 opacity-60'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${
                            notif.notification_type === 'promotion' ? 'bg-orange-100 text-orange-600' :
                            notif.notification_type === 'points' ? 'bg-green-100 text-green-600' :
                            notif.notification_type === 'birthday' ? 'bg-pink-100 text-pink-600' :
                            notif.notification_type === 'reminder' ? 'bg-blue-100 text-blue-600' :
                            'bg-purple-100 text-purple-600'
                          }`}>
                            {notif.notification_type === 'promotion' ? <Tag className="w-4 h-4" /> :
                             notif.notification_type === 'points' ? <Sparkles className="w-4 h-4" /> :
                             notif.notification_type === 'birthday' ? <PartyPopper className="w-4 h-4" /> :
                             notif.notification_type === 'reminder' ? <Bell className="w-4 h-4" /> :
                             <Megaphone className="w-4 h-4" />}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">{notif.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                            {notif.image_url && (
                              <div className="mt-2 rounded-lg overflow-hidden border w-32 h-20">
                                <img 
                                  src={getImageUrl(notif.image_url)} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              <span>
                                {new Date(notif.created_at).toLocaleDateString('es-MX', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {notif.target === 'all' ? 'Todos' :
                                 notif.target === 'tier_bronce' ? '🥉 Bronce' :
                                 notif.target === 'tier_plata' ? '🥈 Plata' :
                                 notif.target === 'tier_oro' ? '🥇 Oro' :
                                 notif.target === 'tier_diamante' ? '💎 Diamante' : notif.target}
                              </Badge>
                              <span className="text-green-600">👁 {notif.read_count || 0} vistas</span>
                              {notif.expires_at && (
                                <span className={`flex items-center gap-1 ${new Date(notif.expires_at) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                                  <Clock className="w-3 h-3" />
                                  {new Date(notif.expires_at) < new Date() 
                                    ? 'Expirada' 
                                    : `Hasta ${new Date(notif.expires_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`
                                  }
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Active Switch */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{notif.is_active !== false ? 'Activa' : 'Inactiva'}</span>
                            <Switch
                              checked={notif.is_active !== false}
                              onCheckedChange={(checked) => toggleNotificationActive(notif, checked)}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteNotification(notif.notification_id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Modal */}
          {showNotificationModal && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
              <Card className="w-full max-w-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-purple-500" />
                    Nueva Notificación
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Tipo de Notificación</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[
                        { id: 'general', label: 'General', icon: '📢', color: 'purple' },
                        { id: 'promotion', label: 'Promoción', icon: '🏷️', color: 'orange' },
                        { id: 'points', label: 'Puntos', icon: '✨', color: 'green' },
                        { id: 'birthday', label: 'Cumpleaños', icon: '🎂', color: 'pink' }
                      ].map(type => (
                        <Button
                          key={type.id}
                          variant={newNotification.notification_type === type.id ? 'default' : 'outline'}
                          onClick={() => setNewNotification({...newNotification, notification_type: type.id})}
                          className={newNotification.notification_type === type.id ? `bg-${type.color}-500` : ''}
                        >
                          {type.icon} {type.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Título *</Label>
                    <Input
                      placeholder="Ej: ¡Nueva Promoción!"
                      value={newNotification.title}
                      onChange={(e) => setNewNotification({...newNotification, title: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Mensaje *</Label>
                    <textarea
                      placeholder="Escribe tu mensaje aquí..."
                      value={newNotification.message}
                      onChange={(e) => setNewNotification({...newNotification, message: e.target.value})}
                      className="w-full mt-1 p-3 border rounded-lg resize-none h-24 text-sm"
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Imagen (opcional)
                    </Label>
                    <div className="mt-2">
                      {newNotification.image_url ? (
                        <div className="relative rounded-lg overflow-hidden border">
                          <img 
                            src={getImageUrl(newNotification.image_url)} 
                            alt="Vista previa" 
                            className="w-full h-32 object-cover"
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setNewNotification({...newNotification, image_url: ''})}
                            className="absolute top-2 right-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleNotificationImageUpload}
                            className="hidden"
                            disabled={uploadingNotifImage}
                          />
                          {uploadingNotifImage ? (
                            <div className="flex flex-col items-center">
                              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                              <span className="text-sm text-purple-600 mt-2">Subiendo...</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <Upload className="w-8 h-8 text-gray-400" />
                              <span className="text-sm text-gray-500 mt-2">Haz clic para subir imagen</span>
                              <span className="text-xs text-gray-400 mt-1">JPG, PNG o WebP (máx. 5MB)</span>
                            </div>
                          )}
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Expiration Date */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Vigencia (opcional)
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="date"
                        value={newNotification.expires_at}
                        onChange={(e) => setNewNotification({...newNotification, expires_at: e.target.value})}
                        className="flex-1"
                        min={new Date().toISOString().split('T')[0]}
                      />
                      {newNotification.expires_at && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setNewNotification({...newNotification, expires_at: ''})}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {newNotification.expires_at 
                        ? `La notificación desaparecerá el ${new Date(newNotification.expires_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}`
                        : 'Sin fecha límite - se mostrará hasta que la elimines'}
                    </p>
                  </div>

                  <div>
                    <Label>Enviar a</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[
                        { id: 'all', label: 'Todos', icon: '👥' },
                        { id: 'tier_bronce', label: 'Bronce', icon: '🥉' },
                        { id: 'tier_plata', label: 'Plata', icon: '🥈' },
                        { id: 'tier_oro', label: 'Oro', icon: '🥇' },
                        { id: 'tier_diamante', label: 'Diamante', icon: '💎' }
                      ].map(target => (
                        <Button
                          key={target.id}
                          variant={newNotification.target === target.id ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNewNotification({...newNotification, target: target.id})}
                          className={newNotification.target === target.id ? 'bg-green-500' : ''}
                        >
                          {target.icon} {target.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowNotificationModal(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={createNotification}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Notificación
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Campaigns Tab - Customer Recovery */}
      {activeTab === 'campaigns' && (
        <div className="space-y-6">
          {/* Header Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${campaignFilter === 'all' ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
              onClick={() => setCampaignFilter('all')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-100">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{allCustomers.length}</p>
                    <p className="text-xs text-muted-foreground">Total Clientes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${campaignFilter === 'with_points' ? 'ring-2 ring-amber-500 bg-amber-50' : ''}`}
              onClick={() => setCampaignFilter('with_points')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100">
                    <Star className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{allCustomers.filter(c => c.points_balance > 0).length}</p>
                    <p className="text-xs text-muted-foreground">Con Puntos</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${campaignFilter === 'no_points' ? 'ring-2 ring-gray-500 bg-gray-50' : ''}`}
              onClick={() => setCampaignFilter('no_points')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-gray-100">
                    <UserMinus className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{allCustomers.filter(c => !c.points_balance || c.points_balance === 0).length}</p>
                    <p className="text-xs text-muted-foreground">Sin Puntos</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${campaignFilter === 'inactive' ? 'ring-2 ring-red-500 bg-red-50' : ''}`}
              onClick={() => setCampaignFilter('inactive')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-red-100">
                    <Clock className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{allCustomers.filter(c => {
                      if (!c.last_visit) return true;
                      const days = Math.floor((new Date() - new Date(c.last_visit)) / (1000*60*60*24));
                      return days > 30;
                    }).length}</p>
                    <p className="text-xs text-muted-foreground">Inactivos +30d</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${campaignFilter === 'new' ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
              onClick={() => setCampaignFilter('new')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-100">
                    <UserPlus className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{allCustomers.filter(c => {
                      if (!c.created_at) return false;
                      const days = Math.floor((new Date() - new Date(c.created_at)) / (1000*60*60*24));
                      return days <= 30;
                    }).length}</p>
                    <p className="text-xs text-muted-foreground">Nuevos (30d)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* WhatsApp Masivo Button */}
          <Card className="border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">WhatsApp Masivo</h3>
                    <p className="text-sm text-muted-foreground">
                      Envía mensajes personalizados con plantillas • Se registra en CRM automáticamente
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={openWhatsAppMassModal}
                  className="bg-green-500 hover:bg-green-600 text-white gap-2"
                >
                  <Send className="w-4 h-4" />
                  Abrir WhatsApp Masivo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Real-time Portal Visits Panel */}
          <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="relative">
                    <Eye className="w-5 h-5 text-green-600" />
                    {recentVisits.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    )}
                  </div>
                  Visitas al Portal en Tiempo Real
                </CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="bg-green-100 text-green-700">
                      Hoy: {visitStats.today_visits}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="bg-blue-100 text-blue-700">
                      Semana: {visitStats.week_visits}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="bg-purple-100 text-purple-700">
                      Únicos: {visitStats.unique_visitors_week}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { loadRecentVisits(); loadVisitStats(); }}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {recentVisits.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No hay visitas recientes en la última hora</p>
                </div>
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {recentVisits.slice(0, 10).map((visit, idx) => (
                    <div
                      key={idx}
                      className="flex-shrink-0 bg-white rounded-lg border p-3 min-w-[180px] shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <Users className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{visit.customer_name}</p>
                          <p className="text-xs text-gray-400">{visit.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <Badge variant="outline" className="text-xs">
                          {getSectionLabel(visit.section)}
                        </Badge>
                        <span className="text-gray-400">{getTimeAgo(visit.visited_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search and Actions Bar */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Buscar por nombre o teléfono..."
                    value={campaignSearch}
                    onChange={(e) => setCampaignSearch(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="campaign-search"
                  />
                </div>
                
                {/* Duplicates Alert Button */}
                {duplicates.total_duplicates > 0 && (
                  <Button
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50 gap-2"
                    onClick={() => setShowDuplicatesModal(true)}
                  >
                    <AlertCircle className="w-4 h-4" />
                    {duplicates.total_duplicates} Duplicados
                  </Button>
                )}

                {/* Normalize All Phones Button */}
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={async () => {
                    if (window.confirm('¿Normalizar todos los teléfonos? Esto quitará +52, espacios y guiones de todos los números.')) {
                      await normalizeAllPhones();
                    }
                  }}
                >
                  <RefreshCw className="w-4 h-4" />
                  Limpiar Teléfonos
                </Button>
                
                <Button
                  className="bg-green-600 hover:bg-green-700 gap-2"
                  onClick={() => {
                    // Get filtered customers with phone
                    const customersToMessage = getFilteredCampaignCustomers().filter(c => c.phone);
                    if (customersToMessage.length === 0) {
                      toast.error('No hay clientes con teléfono en esta selección');
                      return;
                    }
                    toast.success(`${customersToMessage.length} clientes listos para enviar WhatsApp`);
                  }}
                >
                  <Send className="w-4 h-4" />
                  Enviar Tarjeta VIP ({(() => {
                    const filtered = getFilteredCampaignCustomers().filter(c => c.phone);
                    return filtered.length;
                  })()})
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Duplicates Modal */}
          {showDuplicatesModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                <CardHeader className="border-b bg-orange-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-orange-100">
                        <AlertCircle className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <CardTitle>Clientes Duplicados Detectados</CardTitle>
                        <CardDescription>
                          {duplicates.total_duplicates} grupos con {duplicates.total_affected_customers} clientes en total
                        </CardDescription>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowDuplicatesModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0 flex-1 overflow-y-auto">
                  {duplicates.groups.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p className="font-medium">¡No hay duplicados!</p>
                      <p className="text-sm">Tu base de clientes está limpia.</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {duplicates.groups.map((group, idx) => (
                        <div key={idx} className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-orange-50">
                                <Phone className="w-3 h-3 mr-1" />
                                {group.phone_normalized}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {group.count} registros duplicados
                              </span>
                            </div>
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 gap-1"
                              disabled={mergingDuplicates}
                              onClick={() => {
                                const secondaryIds = group.customers
                                  .slice(1)
                                  .map(c => c.customer_id);
                                mergeDuplicates(group.suggested_primary, secondaryIds);
                              }}
                            >
                              {mergingDuplicates ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Merge className="w-4 h-4" />
                              )}
                              Fusionar
                            </Button>
                          </div>
                          
                          <div className="space-y-2">
                            {group.customers.map((customer, cIdx) => (
                              <div
                                key={customer.customer_id}
                                className={`p-3 rounded-lg border ${cIdx === 0 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${cIdx === 0 ? 'bg-green-200' : 'bg-gray-200'}`}>
                                      {cIdx === 0 ? (
                                        <CheckCircle2 className="w-4 h-4 text-green-700" />
                                      ) : (
                                        <Copy className="w-4 h-4 text-gray-500" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium">{customer.name}</p>
                                      <p className="text-xs text-gray-500">{customer.phone}</p>
                                    </div>
                                  </div>
                                  <div className="text-right text-sm">
                                    <p className="font-medium">${customer.total_spent?.toLocaleString() || 0}</p>
                                    <p className="text-xs text-gray-500">{customer.visit_count || 0} visitas</p>
                                  </div>
                                </div>
                                {cIdx === 0 && (
                                  <div className="mt-2 text-xs text-green-700 flex items-center gap-1">
                                    <Star className="w-3 h-3" />
                                    Se mantendrá este registro (mayor gasto)
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                <div className="border-t p-4 bg-gray-50 flex justify-between">
                  <Button
                    variant="outline"
                    onClick={normalizeAllPhones}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Normalizar Todos los Teléfonos
                  </Button>
                  <Button variant="outline" onClick={() => setShowDuplicatesModal(false)}>
                    Cerrar
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Customer List */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Base de Clientes
                  <Badge variant="outline" className="ml-2">
                    {getFilteredCampaignCustomers().length} clientes
                  </Badge>
                </CardTitle>
                <div className="text-sm text-muted-foreground">
                  Total puntos: <span className="font-bold text-amber-600">
                    {getFilteredCampaignCustomers().reduce((sum, c) => sum + (c.points_balance || 0), 0).toLocaleString()} pts
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 border-y text-sm font-medium text-gray-600">
                <div className="col-span-3">Cliente</div>
                <div className="col-span-2">Teléfono</div>
                <div className="col-span-2 text-center">Puntos</div>
                <div className="col-span-2 text-center">Última Visita</div>
                <div className="col-span-1 text-center">
                  <Eye className="w-4 h-4 mx-auto" title="Visitas al Portal" />
                </div>
                <div className="col-span-2 text-right">Acciones</div>
              </div>
              
              {/* Customer Rows */}
              <div className="max-h-[500px] overflow-y-auto divide-y">
                {getFilteredCampaignCustomers().length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No se encontraron clientes</p>
                    <p className="text-sm">Intenta con otro filtro o búsqueda</p>
                  </div>
                ) : (
                  getFilteredCampaignCustomers().map((customer, idx) => {
                    const daysAgo = customer.last_visit ? getDaysAgo(customer.last_visit) : null;
                    const isInactive = daysAgo && daysAgo > 30;
                    const hasPoints = customer.points_balance > 0;
                    
                    return (
                      <div 
                        key={customer.customer_id || idx}
                        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50 transition-colors ${isInactive ? 'bg-red-50/50' : ''}`}
                      >
                        {/* Customer Info */}
                        <div className="col-span-3 flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            hasPoints ? 'bg-amber-100' : 'bg-gray-100'
                          }`}>
                            {hasPoints ? (
                              <Star className="w-5 h-5 text-amber-600" />
                            ) : (
                              <Users className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{customer.name}</p>
                            <p className="text-xs text-gray-500">
                              ${(customer.total_spent || 0).toLocaleString()} gastado
                            </p>
                          </div>
                        </div>
                        
                        {/* Phone */}
                        <div className="col-span-2">
                          {customer.phone ? (
                            <span className="text-sm font-mono">{normalizePhoneDisplay(customer.phone)}</span>
                          ) : (
                            <span className="text-sm text-gray-400">Sin teléfono</span>
                          )}
                        </div>
                        
                        {/* Points */}
                        <div className="col-span-2 text-center">
                          {hasPoints ? (
                            <Badge className="bg-amber-500 hover:bg-amber-600">
                              {customer.points_balance?.toLocaleString()} pts
                            </Badge>
                          ) : (
                            <span className="text-sm text-gray-400">0 pts</span>
                          )}
                        </div>
                        
                        {/* Last Visit */}
                        <div className="col-span-2 text-center">
                          {daysAgo !== null ? (
                            <span className={`text-sm ${isInactive ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                              {daysAgo === 0 ? 'Hoy' : daysAgo === 1 ? 'Ayer' : `${daysAgo} días`}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">Sin visitas</span>
                          )}
                        </div>

                        {/* Portal Visits */}
                        <div className="col-span-1 text-center">
                          {customer.portal_visits ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                              {customer.portal_visits}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="col-span-2 flex justify-end gap-2">
                          {customer.phone && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 gap-1"
                              onClick={() => sendWhatsAppWithTracking(customer, 'vip_card')}
                            >
                              <MessageSquare className="w-3 h-3" />
                              <span className="hidden md:inline">WhatsApp</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-4">
          {/* Redemption Stats */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-green-600">
                  {redemptionHistory.stats?.completed || 0}
                </p>
                <p className="text-gray-500">Canjes Completados</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-3">
                  <Clock className="w-8 h-8 text-yellow-600" />
                </div>
                <p className="text-3xl font-bold text-yellow-600">
                  {redemptionHistory.stats?.pending || 0}
                </p>
                <p className="text-gray-500">Canjes Pendientes</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
                  <Percent className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-blue-600">
                  {redemptionHistory.stats?.completion_rate || 0}%
                </p>
                <p className="text-gray-500">Tasa de Conversión</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Redemptions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historial de Canjes (30 días)</CardTitle>
            </CardHeader>
            <CardContent>
              {(!redemptionHistory.redemptions || redemptionHistory.redemptions.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay canjes registrados en los últimos 30 días</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {redemptionHistory.redemptions.slice(0, 15).map((redemption, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          redemption.status === 'completed' ? 'bg-green-100' :
                          redemption.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                        }`}>
                          {redemption.status === 'completed' ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : redemption.status === 'pending' ? (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{redemption.product_name}</p>
                          <p className="text-sm text-gray-500">{redemption.customer_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={
                          redemption.status === 'completed' ? 'default' :
                          redemption.status === 'pending' ? 'warning' : 'destructive'
                        }>
                          {redemption.points_cost} pts
                        </Badge>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(redemption.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ROI Calculator */}
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-600" />
                Valor del Programa de Lealtad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Puntos en circulación</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {expiringPoints.reduce((sum, c) => sum + (c.points_balance || 0), 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">
                    = ${(expiringPoints.reduce((sum, c) => sum + (c.points_balance || 0), 0) * POINTS_VALUE).toFixed(2)} en valor potencial
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">Impacto estimado</p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Visitas adicionales/mes</span>
                      <span className="font-medium text-green-600">+{Math.ceil(expiringPoints.length * 0.3)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Ticket promedio mayor</span>
                      <span className="font-medium text-green-600">+15%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Retención de clientes</span>
                      <span className="font-medium text-green-600">+25%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Image Upload Modal for Products */}
      {showImageModal && selectedProductForImage && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-green-500" />
                Subir Imagen
              </CardTitle>
              <CardDescription>
                {selectedProductForImage.name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Image Preview */}
              {selectedProductForImage.image_url && (
                <div className="rounded-lg overflow-hidden border">
                  <img 
                    src={getImageUrl(selectedProductForImage.image_url)} 
                    alt="Imagen actual" 
                    className="w-full h-40 object-cover"
                  />
                  <p className="text-xs text-gray-500 p-2 bg-gray-50">Imagen actual</p>
                </div>
              )}

              {/* Upload Area */}
              <div>
                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleProductImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  {uploadingImage ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-10 h-10 text-green-500 animate-spin" />
                      <span className="text-sm text-green-600 mt-2">Subiendo imagen...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-10 h-10 text-gray-400" />
                      <span className="text-sm text-gray-600 mt-2 font-medium">
                        Haz clic para seleccionar imagen
                      </span>
                      <span className="text-xs text-gray-400 mt-1">JPG, PNG o WebP (máx. 5MB)</span>
                    </div>
                  )}
                </label>
              </div>

              <p className="text-xs text-gray-500 text-center">
                La imagen se guardará permanentemente en el sistema y aparecerá en la Tarjeta VIP del cliente.
              </p>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowImageModal(false);
                    setSelectedProductForImage(null);
                  }}
                  className="flex-1"
                  disabled={uploadingImage}
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* WhatsApp Mass Messaging Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">WhatsApp Masivo - Programa de Lealtad</h2>
                  <p className="text-green-100 text-sm">Envía mensajes personalizados a tus clientes VIP</p>
                </div>
              </div>
              <button 
                onClick={() => setShowWhatsAppModal(false)}
                className="text-white/80 hover:text-white p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex h-[calc(90vh-80px)]">
              {/* Left Panel - Settings */}
              <div className="w-1/3 border-r bg-gray-50 p-4 space-y-4 overflow-y-auto">
                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📝 Plantilla de Mensaje
                  </label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full p-3 border rounded-lg bg-white focus:ring-2 focus:ring-green-500"
                  >
                    {Object.entries(MESSAGE_TEMPLATES).map(([key, tmpl]) => (
                      <option key={key} value={key}>{tmpl.name}</option>
                    ))}
                  </select>
                </div>

                {/* Filter Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    👥 Filtrar Clientes
                  </label>
                  <div className="space-y-2">
                    <button
                      onClick={() => { setWhatsAppFilterType('all'); setWhatsAppCustomers(allCustomers.filter(c => c.phone)); }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        whatsAppFilterType === 'all' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Todos
                      </span>
                      <span className="text-sm text-gray-500">{allCustomers.filter(c => c.phone).length}</span>
                    </button>
                    <button
                      onClick={() => { setWhatsAppFilterType('with_points'); setWhatsAppCustomers(allCustomers.filter(c => c.phone && (c.points_balance || 0) > 0)); }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        whatsAppFilterType === 'with_points' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-amber-500" />
                        Con Puntos
                      </span>
                      <span className="text-sm text-gray-500">{allCustomers.filter(c => c.phone && (c.points_balance || 0) > 0).length}</span>
                    </button>
                    <button
                      onClick={() => { setWhatsAppFilterType('no_points'); setWhatsAppCustomers(allCustomers.filter(c => c.phone && (!c.points_balance || c.points_balance === 0))); }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        whatsAppFilterType === 'no_points' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <UserMinus className="w-4 h-4 text-gray-500" />
                        Sin Puntos
                      </span>
                      <span className="text-sm text-gray-500">{allCustomers.filter(c => c.phone && (!c.points_balance || c.points_balance === 0)).length}</span>
                    </button>
                    <button
                      onClick={() => { 
                        setWhatsAppFilterType('inactive'); 
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        setWhatsAppCustomers(allCustomers.filter(c => c.phone && (!c.last_visit || new Date(c.last_visit) < thirtyDaysAgo))); 
                      }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        whatsAppFilterType === 'inactive' ? 'bg-orange-100 border-2 border-orange-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-500" />
                        Inactivos (+30 días)
                      </span>
                      <span className="text-sm text-orange-600">{allCustomers.filter(c => {
                        if (!c.phone) return false;
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        return !c.last_visit || new Date(c.last_visit) < thirtyDaysAgo;
                      }).length}</span>
                    </button>
                    
                    {/* Contact Status Filters */}
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-gray-500 mb-2 font-medium">SEGUIMIENTO CRM</p>
                      <button
                        onClick={() => { 
                          setWhatsAppFilterType('not_contacted'); 
                          setWhatsAppCustomers(allCustomers.filter(c => c.phone && !contactStatus[c.customer_id]?.contacted)); 
                        }}
                        className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                          whatsAppFilterType === 'not_contacted' ? 'bg-orange-100 border-2 border-orange-500' : 'bg-white border'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-orange-500" />
                          Sin Contactar
                        </span>
                        <span className="text-sm text-orange-600 font-medium">
                          {allCustomers.filter(c => c.phone && !contactStatus[c.customer_id]?.contacted).length}
                        </span>
                      </button>
                      <button
                        onClick={() => { 
                          setWhatsAppFilterType('contacted'); 
                          setWhatsAppCustomers(allCustomers.filter(c => c.phone && contactStatus[c.customer_id]?.contacted)); 
                        }}
                        className={`w-full p-3 rounded-lg text-left flex items-center justify-between mt-2 ${
                          whatsAppFilterType === 'contacted' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          Ya Contactados
                        </span>
                        <span className="text-sm text-green-600 font-medium">
                          {allCustomers.filter(c => c.phone && contactStatus[c.customer_id]?.contacted).length}
                        </span>
                      </button>
                      <button
                        onClick={() => { 
                          setWhatsAppFilterType('no_vip_card'); 
                          setWhatsAppCustomers(allCustomers.filter(c => c.phone && !contactStatus[c.customer_id]?.vipCardSent)); 
                        }}
                        className={`w-full p-3 rounded-lg text-left flex items-center justify-between mt-2 ${
                          whatsAppFilterType === 'no_vip_card' ? 'bg-purple-100 border-2 border-purple-500' : 'bg-white border'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-purple-500" />
                          Sin Tarjeta VIP
                        </span>
                        <span className="text-sm text-purple-600 font-medium">
                          {allCustomers.filter(c => c.phone && !contactStatus[c.customer_id]?.vipCardSent).length}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    👁️ Vista Previa del Mensaje
                  </label>
                  <div className="bg-[#e5ddd5] p-3 rounded-lg">
                    <div className="bg-white rounded-lg p-3 shadow-sm max-h-60 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap font-sans">
                        {generateMessage({ name: 'Juan', points_balance: 500, visit_count: 5 }, selectedTemplate)}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Customer List */}
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b bg-white space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">
                      📱 {whatsAppCustomers.filter(c => {
                        if (!whatsAppSearch) return true;
                        const search = whatsAppSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(search) || 
                               (c.phone || '').includes(search);
                      }).length} clientes seleccionados
                    </p>
                    <p className="text-sm text-gray-500">
                      Clic en 📋 para copiar o ▶️ para abrir WhatsApp
                    </p>
                  </div>
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre o teléfono..."
                      value={whatsAppSearch}
                      onChange={(e) => setWhatsAppSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="whatsapp-masivo-search-loyalty"
                    />
                    {whatsAppSearch && (
                      <button
                        onClick={() => setWhatsAppSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2">
                    {whatsAppCustomers
                      .filter(c => {
                        if (!whatsAppSearch) return true;
                        const search = whatsAppSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(search) || 
                               (c.phone || '').includes(search);
                      })
                      .map(customer => (
                      <div 
                        key={customer.customer_id}
                        className="bg-white border rounded-lg p-3 flex items-center justify-between hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <UserCheck className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {customer.name}
                              {contactStatus[customer.customer_id]?.contacted && (
                                <span className="text-green-500" title="Ya contactado">✓</span>
                              )}
                              {contactStatus[customer.customer_id]?.vipCardSent && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600">VIP</Badge>
                              )}
                            </p>
                            <p className="text-sm text-gray-500 flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {normalizePhoneDisplay(customer.phone)}
                              {(customer.points_balance || 0) > 0 && (
                                <span className="text-amber-600 font-medium">
                                  ⭐ {(customer.points_balance || 0).toLocaleString()} pts
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyMessage(customer)}
                            className="h-9"
                          >
                            {copiedId === customer.customer_id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openWhatsAppMass(customer)}
                            disabled={sendingToCustomer === customer.customer_id}
                            className="h-9 bg-green-500 hover:bg-green-600 text-white"
                          >
                            {sendingToCustomer === customer.customer_id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-1" />
                                Enviar
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}

                    {whatsAppCustomers
                      .filter(c => {
                        if (!whatsAppSearch) return true;
                        const search = whatsAppSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(search) || 
                               (c.phone || '').includes(search);
                      }).length === 0 && (
                      <div className="text-center py-10 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>{whatsAppSearch ? 'No se encontraron clientes con ese término' : 'No hay clientes con este filtro'}</p>
                        {whatsAppSearch && (
                          <button 
                            onClick={() => setWhatsAppSearch('')}
                            className="mt-2 text-green-600 hover:text-green-700 text-sm"
                          >
                            Limpiar búsqueda
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50">
                  <p className="text-xs text-gray-500 text-center">
                    💡 Al enviar, se registrará automáticamente en el seguimiento CRM del cliente
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyManager;
